import * as React from "react";
import { useState } from "react";
import {
    Card,
    CardActionArea,
    CardContent,
    Grid,
    Typography,
    IconButton,
    Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import WarningIcon from "@material-ui/icons/Warning";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import StarIcon from "@material-ui/icons/Star";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import { Notification } from "react-admin";

import ShopGridCategories from "./ShopGridCategories";
import AssignCategory from "./shopgrid/AssignCategory";
import FeaturedManager from "./shopgrid/FeaturedManager";
import SpendManager from "./shopgrid/SpendManager";

const useStyles = makeStyles((theme) => ({
    root: { padding: theme.spacing(3), backgroundColor: "#f5f5f5", minHeight: "100vh" },
    banner: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        backgroundColor: "#fff4e5",
        color: "#663c00",
        border: "1px solid #ffb74d",
        borderRadius: 8,
        padding: theme.spacing(1.5, 2),
        marginBottom: theme.spacing(3),
    },
    bannerIcon: { color: "#ed6c02" },
    title: { marginBottom: theme.spacing(3), fontWeight: 700 },
    tile: {
        height: "100%",
        borderRadius: 12,
        transition: "transform .12s ease, box-shadow .12s ease",
        "&:hover": { transform: "translateY(-3px)", boxShadow: theme.shadows[6] },
    },
    tileArea: { height: "100%", padding: theme.spacing(1) },
    tileContent: { display: "flex", flexDirection: "column", gap: theme.spacing(1) },
    iconWrap: {
        width: 48,
        height: 48,
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
        marginBottom: theme.spacing(1),
    },
    tileTitle: { fontWeight: 700 },
    tileDesc: { color: "#666" },
    detailHeader: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    detailBody: {
        backgroundColor: "#fff",
        borderRadius: 12,
        padding: theme.spacing(2),
    },
}));

const UK_SPEND_PROMPT = `For the brands listed below, give me your best ESTIMATE of total UK consumer spend over the last 90 days (L90D), in GBP.

Rules:
- Estimate only — I know these figures can't be precise; an approximate value is exactly what I want.
- Use your own estimate. Do NOT read from or cite any third-party source that provides this data.
- If you have no reasonable basis to estimate a brand, SKIP it (don't guess wildly).

Output — return a CSV I can upload directly:
- One \`\`\`csv code block, with this exact header row:
brand,uk_spend
- One row per ESTIMATED brand; keep the brand spelled exactly as given.
- uk_spend must be a plain whole number in GBP — no £ sign, no commas, no thousands separators (e.g. 775000000, not £775,000,000).
- Do NOT put skipped brands in the CSV. List every skipped brand together AFTER the code block.

Brands:
<paste the "brand" column from the downloaded CSV here>`;

const SPRIVE_SPEND_PROMPT = `-- L30D Sprive sales per brand: Tillo gift card purchases + gift card top-ups + VDX purchases
SELECT brand, ROUND(SUM(amount), 2) AS sprive_sales
FROM (
  -- Tillo gift card purchases
  SELECT COALESCE(gbm.brand_name_override, gc.brand_name) AS brand,
         t.face_value_amount AS amount
  FROM gift_card_user_purchase_transaction t
  JOIN gift_card gc ON gc.id = t.gift_card_id
  LEFT JOIN gift_card_brand_details_mapping gbm ON gbm.brand_name = gc.brand_name
  WHERE t.status = 'success'
    AND t.createdAt >= NOW() - INTERVAL 30 DAY
  UNION ALL
  -- Tillo gift card top-ups
  SELECT COALESCE(gbm.brand_name_override, gc.brand_name) AS brand,
         tp.face_value_amount AS amount
  FROM gift_card_topup_transaction tp
  JOIN gift_card gc ON gc.id = tp.gift_card_id
  LEFT JOIN gift_card_brand_details_mapping gbm ON gbm.brand_name = gc.brand_name
  WHERE tp.status = 'success'
    AND tp.createdAt >= NOW() - INTERVAL 30 DAY
  UNION ALL
  -- VDX purchases
  SELECT CONVERT(v.merchant_name USING utf8mb4) COLLATE utf8mb4_unicode_ci AS brand,
         v.amount AS amount
  FROM vdx_user_purchases v
  WHERE v.status <> 'cancelled'
    AND v.purchase_date >= NOW() - INTERVAL 30 DAY
) s
GROUP BY brand
ORDER BY sprive_sales DESC;`;

const SECTIONS = [
    {
        id: "grid",
        title: "Categories & Grid",
        description: "Order, rename, pin and activate categories; manage each category's brand sequence.",
        Icon: ViewModuleIcon,
        render: () => <ShopGridCategories embedded />,
    },
    {
        id: "assign",
        title: "Assign Child Category",
        description: "Move a brand into a sub-category (sets its Sprive category).",
        Icon: AccountTreeIcon,
        render: () => <AssignCategory />,
    },
    {
        id: "featured",
        title: "Featured",
        description: "Choose the featured brands and their order.",
        Icon: StarIcon,
        render: () => <FeaturedManager />,
    },
    {
        id: "spend",
        title: "Spend (UK / Sprive)",
        description: "Bulk-update UK spend (L90D) and Sprive spend (L30D) via CSV.",
        Icon: TrendingUpIcon,
        render: () => (
            <SpendManager
                metrics={[
                    {
                        field: "uk_spend",
                        title: "UK spend",
                        windowLabel: "L90D",
                        promptTitle:
                            "Estimate uk_spend — paste this into Claude; it returns a ready-to-upload CSV:",
                        prompt: UK_SPEND_PROMPT,
                    },
                    {
                        field: "sprive_sales",
                        title: "Sprive spend",
                        windowLabel: "L30D",
                        promptTitle:
                            "Fill the sprive_sales column — run this SQL query (L30D gift card purchases + top-ups + VDX), then put the results in the CSV:",
                        prompt: SPRIVE_SPEND_PROMPT,
                    },
                ]}
            />
        ),
    },
];

const ShopGridManager = () => {
    const classes = useStyles();
    const [active, setActive] = useState<string | null>(null);

    const current = SECTIONS.find((s) => s.id === active);

    return (
        <div className={classes.root}>
            <Box className={classes.banner}>
                <WarningIcon className={classes.bannerIcon} fontSize="small" />
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                    This screen is a work in progress and is not fully ready for production use.
                </Typography>
            </Box>
            {!current ? (
                <>
                    <Typography variant="h5" className={classes.title}>
                        Shop Grid
                    </Typography>
                    <Grid container spacing={3}>
                        {SECTIONS.map((s) => {
                            const Icon = s.Icon;
                            return (
                                <Grid item xs={12} sm={6} md={4} key={s.id}>
                                    <Card className={classes.tile} elevation={2}>
                                        <CardActionArea
                                            className={classes.tileArea}
                                            onClick={() => setActive(s.id)}
                                        >
                                            <CardContent className={classes.tileContent}>
                                                <div className={classes.iconWrap}>
                                                    <Icon />
                                                </div>
                                                <Typography variant="subtitle1" className={classes.tileTitle}>
                                                    {s.title}
                                                </Typography>
                                                <Typography variant="body2" className={classes.tileDesc}>
                                                    {s.description}
                                                </Typography>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </>
            ) : (
                <>
                    <Box className={classes.detailHeader}>
                        <IconButton onClick={() => setActive(null)} aria-label="back">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h5" style={{ fontWeight: 700 }}>
                            {current.title}
                        </Typography>
                    </Box>
                    <div className={classes.detailBody}>{current.render()}</div>
                </>
            )}
            <Notification />
        </div>
    );
};

export default ShopGridManager;
